import { useTranslation } from "react-i18next"
import { useSuspenseQueryGet } from "../../../../../typed-client/hooks/use-suspense-query-get"
import { MRT_ColumnDef, MRT_VisibilityState } from "mantine-react-table"
import { IconCheckBox } from "../../../-components/icon-check-box"
import { MRT_DateInputFilter } from "../../../-components/infinit-scrolling-table/mrt_date-input-filter"
import { DateOnly } from "../../../../../@client/System"
import {
  ProductionDto,
  ProductionFieldDto,
} from "../../../../../@client/Theasoft_ApiServer_Productions"
import { getFormattedDate } from "../../../../../utils/get-formatted-date"
import { FieldData } from "../../../../../-components/field-data"

export function useProductionTableColumns() {
  const { t } = useTranslation("app_server")

  const { data: productionFieldTypes } = useSuspenseQueryGet(
    "/api/Production/GetProductionFieldTypesForTable",
    null
  )
  const baseColumns = React.useMemo<MRT_ColumnDef<ProductionDto>[]>(
    () => [
      {
        accessorKey: "title",
        header: t("Title"),
      },
      { accessorKey: "originalTitle", header: t("OriginalTitle") },
      {
        accessorKey: "genre",
        header: t("Genre"),
        enableColumnFilter: false,
      },
      {
        accessorKey: "isInactiv",
        header: t("Played"),
        Cell: ({ cell }) => <IconCheckBox checked={cell.getValue<boolean>()} />,
        filterVariant: "checkbox",
      },
      {
        accessorKey: "premiere",
        header: t("Premiere"),
        Cell: ({ cell }) => getFormattedDate(cell.getValue<DateOnly>()),
        Filter: ({ column }) => <MRT_DateInputFilter column={column} />,
      },
      { accessorKey: "stage", header: t("Stage") },
    ],
    []
  )

  const extendedColumns = React.useMemo<MRT_ColumnDef<ProductionDto>[]>(
    () => [
      { accessorKey: "id", header: "Id" },
      ...baseColumns,
      {
        accessorKey: "work",
        header: t("Work"),
      },
      ...productionFieldTypes.map<MRT_ColumnDef<ProductionDto>>(
        (productionFieldType) => ({
          accessorFn: (row) =>
            row.productionFields?.find(
              (productionField) =>
                productionField?.productionFieldTypeId ===
                productionFieldType.productionFieldTypeId
            ),
          header: productionFieldType.name,
          Cell: ({ cell }) => {
            const cellValue = cell.getValue<ProductionFieldDto | undefined>()
            return (
              cellValue && (
                <FieldData
                  fieldData={{
                    textValue: cellValue.value,
                    dataType: cellValue.fieldDataType,
                  }}
                />
              )
            )
          },
          enableSorting: false,
          enableColumnFilter: false,
          id: "productionField-" + productionFieldType.productionFieldTypeId,
        })
      ),
    ],
    []
  )

  const defaultColumnVisibility = React.useMemo<MRT_VisibilityState>(
    () => ({
      id: false,
      work: false,
      ...productionFieldTypes.reduce(
        (acc, v) => {
          acc[`productionField-${v.productionFieldTypeId}`] = false
          return acc
        },
        {} as Record<string, boolean>
      ),
    }),
    [productionFieldTypes]
  )

  return { baseColumns, extendedColumns, defaultColumnVisibility }
}
